import React, { FC, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import en from 'src/constants/en'
import { NEW_CAMPAIGN_ROUTE } from 'src/constants/routes'
import Button from 'src/components/Button'
import { CheckBox } from 'src/components/CheckBox'
import { FetchCampaignsResponse } from 'src/services/__generated__/api'
import { Card } from 'src/components/Card'
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg'
import styles from './chooseCampaignModalContent.module.scss'

export interface ChooseCampaignModalContentProps {
  campaignsData?: FetchCampaignsResponse
  closeAndClear: () => void
  handleNextModal: () => void
  selectedCampaignForNewKeyword?: string[]
  setSelectedCampaignForNewKeyword: (id: string[]) => void
}

export const ChooseCampaignModalContent: FC<
  ChooseCampaignModalContentProps
> = ({
  campaignsData,
  selectedCampaignForNewKeyword,
  setSelectedCampaignForNewKeyword,
  closeAndClear,
  handleNextModal,
}) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const campaigns = campaignsData?.campaigns || []

  const filteredCampaigns = useMemo(() => {
    if (!searchQuery.trim()) {
      return campaigns
    }
    const query = searchQuery.toLowerCase()
    return campaigns.filter(
      (campaign) =>
        campaign.name?.toLowerCase().includes(query) ||
        campaign.status?.toLowerCase().includes(query)
    )
  }, [campaigns, searchQuery])

  const allCampaignIds = campaigns
    .map((c) => c._id)
    .filter((id): id is string => !!id)

  const selectedCount = selectedCampaignForNewKeyword?.length || 0

  const areAllSelected =
    allCampaignIds.length > 0 &&
    allCampaignIds.every((id) => selectedCampaignForNewKeyword?.includes(id))

  const handleSelectAll = () => {
    if (areAllSelected) {
      setSelectedCampaignForNewKeyword([])
    } else {
      setSelectedCampaignForNewKeyword(allCampaignIds)
    }
  }

  const handleToggleCampaign = (campaignId: string) => {
    if (selectedCampaignForNewKeyword?.includes(campaignId)) {
      setSelectedCampaignForNewKeyword(
        selectedCampaignForNewKeyword.filter((id) => id !== campaignId)
      )
    } else {
      setSelectedCampaignForNewKeyword([
        ...(selectedCampaignForNewKeyword || []),
        campaignId,
      ])
    }
  }

  const getSelectionLabel = () => {
    if (areAllSelected) {
      return `${en.allCampaigns} (${allCampaignIds.length})`
    }
    if (selectedCount === 1) {
      const selectedCampaign = campaigns.find(
        (c) => c._id === selectedCampaignForNewKeyword?.[0]
      )
      return selectedCampaign?.name || ''
    }
    if (selectedCount > 1) {
      return `${selectedCount} ${en.campaigns}`
    }
    return ''
  }

  return (
    <Card className={styles.chooseCampaignModalContent}>
      <div
        className={classNames(
          styles.modalHeader,
          campaigns.length === 0 && styles.centered
        )}
      >
        {campaigns.length === 0 ? (
          <img alt="Hapttic" className={styles.logo} src="/logo.png" />
        ) : (
          <>
            <span>{en.addNewKeyword}</span>
            <button
              aria-label="Close"
              className={styles.closeButton}
              onClick={closeAndClear}
              type="button"
            >
              ×
            </button>
          </>
        )}
      </div>

      <div className={styles.modalBody}>
        {campaigns.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIconContainer}>
              <WarningIcon className={styles.emptyStateIcon} />
            </div>
            <h4 className={styles.emptyStateTitle}>Create a campaign first</h4>
            <p className={styles.emptyStateText}>
              Create a campaign first. Keywords can be added only after a
              campaign is created. Please set up a campaign to continue.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.label}>{en.keyword}</div>

            <div className={styles.campaignListContainer}>
              <div className={styles.campaignListHeader}>
                <div className={styles.searchInputWrapper}>
                  <input
                    className={styles.searchInput}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={en.campaign}
                    type="text"
                    value={searchQuery}
                  />
                </div>
                <div className={styles.selectedCount}>
                  <span className={styles.selectedCountNumber}>
                    {selectedCount}
                  </span>{' '}
                  {en.selected}
                </div>
              </div>

              <div className={styles.campaignList}>
                <div
                  className={classNames(
                    styles.listItem,
                    styles.allCampaignsItem,
                    areAllSelected && styles.listItemActive
                  )}
                  onClick={handleSelectAll}
                >
                  <div className={styles.allCampaignsCheckbox}>
                    <CheckBox isChecked={areAllSelected} isDouble />
                  </div>
                  <span className={styles.campaignName}>{en.allCampaigns}</span>
                </div>

                {filteredCampaigns.map((item) => {
                  const isActive =
                    !!item._id &&
                    !!selectedCampaignForNewKeyword?.includes(item._id)

                  return (
                    <div
                      aria-current={isActive}
                      className={classNames(
                        styles.listItem,
                        isActive && styles.listItemActive
                      )}
                      key={item._id}
                      onClick={() => item._id && handleToggleCampaign(item._id)}
                    >
                      <div className={styles.campaignCheckbox}>
                        <CheckBox isChecked={isActive} />
                      </div>
                      <span className={styles.campaignName}>{item.name}</span>
                      {item.status && (
                        <>
                          <span className={styles.separator}>·</span>
                          <span className={styles.campaignStatus}>
                            {item.status}
                          </span>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className={styles.selectionSummary}>
              {selectedCount > 0 && (
                <>
                  {en.selectedCampaign}: <strong>{getSelectionLabel()}</strong>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div
        className={classNames(
          styles.modalFooter,
          campaigns.length === 0 && styles.centered
        )}
      >
        {campaigns.length === 0 ? (
          <>
            <button
              className={styles.cancel}
              onClick={closeAndClear}
              type="button"
            >
              Discard
            </button>
            {/* Closes the modal before navigating: BaseModal locks body
                scroll while open, and leaving it mounted would carry that
                lock onto the campaign page. */}
            <Button
              onClick={() => {
                closeAndClear()
                navigate(NEW_CAMPAIGN_ROUTE)
              }}
              type="button"
            >
              Create a campaign
            </Button>
          </>
        ) : (
          <>
            <button
              className={styles.cancel}
              onClick={closeAndClear}
              type="button"
            >
              {en.cancel}
            </button>
            <Button
              isDisabled={selectedCount === 0}
              onClick={handleNextModal}
              type="button"
            >
              {en.next}
            </Button>
          </>
        )}
      </div>
    </Card>
  )
}
